package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.MenuViewModel
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.rememberUseNativeStyleWebLayout
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p1
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p14
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import com.example.mystybag.core.views.AppTheme
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

// Figma: Account screen – width 412, content Frame 104 width 380, itemSpacing 32
// Section headers 12dp + 1px divider; List items 60dp, padding top 24 bottom 12, itemSpacing 8
// Card cornerRadius 20, padding 20; Danger button cornerRadius 100, height 56, padding 10

@Composable
fun MenuView(viewModel: MenuViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()
    val colors = AppTheme.current.colors
    val useNativeStyleWebLayout = rememberUseNativeStyleWebLayout()

    UIView(
        modifier = Modifier
            .fillMaxSize()
            .background(colors.background),
        viewModel = viewModel,
        showTopBar = false,
        applyBottomInsetSpacer = false,
        removeVerticalPadding = true
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            when {
                uiState.isLoading -> {
                    Box(
                        modifier = Modifier.fillMaxSize(),
                        contentAlignment = Alignment.Center
                    ) {
                        CircularProgressIndicator(
                            modifier = Modifier.size(p24),
                            color = colors.primary
                        )
                    }
                }
                else -> {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(start = p16, end = p16, top = p16),
                        horizontalAlignment = Alignment.Start
                    ) {
                        if (useNativeStyleWebLayout) {
                            Texts.DisplaySmall(
                                text = stringResource(SR.strings.menu),
                                modifier = Modifier.fillMaxWidth(),
                                color = colors.onSurface
                            )
                            Spacer(modifier = Modifier.height(p12))
                        }
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .weight(1f)
                                .verticalScroll(rememberScrollState())
                                .scrollEdgeFade(showTop = false)
                        ) {
                            Column(
                                modifier = Modifier.fillMaxWidth(),
                                verticalArrangement = Arrangement.spacedBy(p24)
                            ) {
                                // List Group 1 – Finance (section_account / finance)
                                AccountSection(
                                    sectionTitle = sharedString(SR.strings.finance),
                                    content = {
                                        AccountListItem(
                                            text = sharedString(SR.strings.statements),
                                            onClick = { viewModel.navigateToStatements() }
                                        )
                                        // Bank Account card: cornerRadius 20, padding 20
                                        Card(
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .clickable { viewModel.navigateToBankAccount() },
                                            shape = RoundedCornerShape(p20),
                                            elevation = CardDefaults.cardElevation(defaultElevation = 2.dp),
                                            colors = CardDefaults.cardColors(containerColor = AppTheme.current.colors.surfaceContainerLow)
                                        ) {
                                            Column(
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .padding(p20)
                                            ) {
                                                Row(
                                                    modifier = Modifier.fillMaxWidth(),
                                                    horizontalArrangement = Arrangement.SpaceBetween,
                                                    verticalAlignment = Alignment.CenterVertically
                                                ) {
                                                    Column(modifier = Modifier.weight(1f)) {
                                                        Texts.TitleMedium(text = sharedString(SR.strings.bank_account))
                                                        val iban = uiState.iban
                                                        if (iban != null && iban.isNotEmpty()) {
                                                            Spacer(modifier = Modifier.height(p4))
                                                            Texts.BodySmall(text = iban)
                                                            if (uiState.ibanConfirmationDate != null) {
                                                                Texts.BodySmall(
                                                                    text = "${sharedString(SR.strings.confirmed_on)}: ${uiState.ibanConfirmationDate!!.substringBefore("T")}",
                                                                    color = AppTheme.current.colors.primary
                                                                )
                                                            } else {
                                                                Texts.BodySmall(
                                                                    text = sharedString(SR.strings.pending_confirmation),
                                                                    color = AppTheme.current.colors.error
                                                                )
                                                            }
                                                        } else {
                                                            Spacer(modifier = Modifier.height(p4))
                                                            Texts.BodySmall(
                                                                text = sharedString(SR.strings.no_iban_set),
                                                                color = AppTheme.current.colors.error
                                                            )
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                )

                                // List Group 2 – Business
                                AccountSection(
                                    sectionTitle = sharedString(SR.strings.business),
                                    content = {
                                        AccountListItem(
                                            text = sharedString(SR.strings.merchant_profile),
                                            onClick = { viewModel.navigateToMerchantProfile() }
                                        )
                                        if (!uiState.isStaff) {
                                            AccountListItem(
                                                text = sharedString(SR.strings.staff),
                                                onClick = { viewModel.navigateToStaffList() }
                                            )
                                        }
                                        AccountListItem(
                                            text = sharedString(SR.strings.company_responsible_persons),
                                            onClick = { viewModel.navigateToCompanyResponsiblePersons() }
                                        )
                                    }
                                )

                                // List Group 3 – Contacts
                                AccountSection(
                                    sectionTitle = "MYSTYBAG",
                                    content = {
                                        AccountListItem(
                                            text = sharedString(SR.strings.settings),
                                            onClick = { viewModel.navigateToSettings() }
                                        )
                                        AccountListItem(
                                            text = sharedString(SR.strings.about_mystybag),
                                            onClick = {  }
                                        )
                                        AccountListItem(
                                            text = sharedString(SR.strings.terms_of_use),
                                            onClick = {  }
                                        )
                                        AccountListItem(
                                            text = sharedString(SR.strings.privacy_policy),
                                            onClick = {  }
                                        )
                                        AccountListItem(
                                            text = sharedString(SR.strings.contacts),
                                            onClick = { viewModel.navigateToContacts() }
                                        )
                                    }
                                )
                            }

                            Spacer(modifier = Modifier.height(p32))

                            // Log out button: half width, centered (same as ProfilePageView)
                            Box(
                                modifier = Modifier.fillMaxWidth(),
                                contentAlignment = Alignment.Center
                            ) {
                                DefaultButton(
                                    modifier = Modifier
                                        .fillMaxWidth(0.5f)
                                        .height(p56)
                                        .padding(horizontal = p0),
                                    caption = sharedString(SR.strings.log_out),
                                    type = DefaultButtonType.SECONDARY_RED,
                                    backgroundColor = Color(0xFFFFE4E4),
                                    textColor = AppTheme.current.colors.error,
                                    callback = { viewModel.onLogoutClicked() }
                                )
                            }

                            Spacer(modifier = Modifier.height(p32))

                            // App version: 380x16
                            Texts.BodySmall(
                                text = sharedString(SR.strings.app_version, uiState.appVersion),
                                color = AppTheme.current.colors.onSurfaceVariant,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(bottom = p16)
                                    .height(p16),
                                textAlign = TextAlign.Center
                            )
                        }
                    }
                }
            }
        }
    }
}

/*
 * Existing section/list item composables below.
 */
@Composable
private fun AccountSection(
    sectionTitle: String,
    content: @Composable () -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(p0)
    ) {
        // Section Header: 12dp spacing + 1px line (Figma)
        Column(
            modifier = Modifier.fillMaxWidth().padding(bottom = p8, top = p8),
            verticalArrangement = Arrangement.spacedBy(p12)
        ) {
            Texts.LabelLarge(
                text = sectionTitle.uppercase(),
                color = AppTheme.current.colors.onSurfaceVariant,
                modifier = Modifier.fillMaxWidth()
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(p1)
                    .background(AppTheme.current.colors.outline)
            )
        }
        Column(
            modifier = Modifier.fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(p8)
        ) {
            content()
        }
    }
}

@Composable
private fun AccountListItem(
    text: String,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(p48)
            .rippleClick(padding = p8, roundPadding = p8, action = onClick)
    ) {
        Texts.TitleMedium(
            text = text,
            color = AppTheme.current.colors.onSurface,
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.CenterStart)
        )
        androidx.compose.foundation.Image(
            painter = painterResource(SRC.images.right_arrow),
            contentDescription = null,
            contentScale = androidx.compose.ui.layout.ContentScale.Fit,
            modifier = Modifier
                .size(width = p8, height = p14)
                .align(Alignment.CenterEnd),
            colorFilter = ColorFilter.tint(AppTheme.current.colors.primary)
        )
    }
}

